import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import JSZip from "jszip";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { batchFileName, contrastWarning, longQrValues, parseBulkLines } from "@/lib/qrHelpers";
import { toCsv } from "@/lib/csv";
import { ProjectBar } from "@/components/ProjectBar";
import { useProject } from "@/lib/workflow/useProject";
import { makePassCode } from "@/lib/workflow/passCode";
import { BADGE_WHO_LABEL, badgesFromGuests, type BadgeWho } from "../events/badgeData";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates many QR codes at once from a list of URLs or text entries and downloads them together as one ZIP file.`,
  how: `Paste your list (one entry per line), choose size, colours and format, then click Generate. You get a single ZIP with one numbered file per code plus an index.csv that maps each file to its value. To name the files yourself, tick "Lines are name,value" (a line that starts with a comma has no name, and the comma is not put in the code). A note appears when a value is longer than about 300 characters, because such codes are dense and may not scan.`,
  tip: `Keep the index.csv: file names are numbered in the order of your list, so the CSV is the quickest way to match each image back to what it links to. Batches are limited to 500 codes.`,
};

const MAX_BATCH = 500;
const PREVIEW_COUNT = 12;

export default function BulkQr() {
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [named, setNamed] = useState(false);
  const [size, setSize] = useState("600");
  const [fg, setFg] = useState("#000000");
  const [bg, setBg] = useState("#ffffff");
  const [ec, setEc] = useState<"L" | "M" | "Q" | "H">("M");
  const [fmt, setFmt] = useState<"png" | "svg">("png");

  const { logUsageNow } = useLogUsageOnAction();

  const [project] = useProject();
  const [mode, setMode] = useState<"list" | "passes">("list");
  const [who, setWho] = useState<BadgeWho>("coming");
  const [passRows, setPassRows] = useState<{ name: string | null; value: string }[]>([]);
  const guestsToPass = useMemo(() => badgesFromGuests(project.data.guests, who), [project.data.guests, who]);
  // Signed pass codes: a random guest ID plus a signature from the project key. No name or email inside the code.
  useEffect(() => {
    if (mode !== "passes") return;
    let live = true;
    Promise.all(guestsToPass.map(async g => ({ name: g.name as string | null, value: await makePassCode(project.passKey, g.id!) })))
      .then(list => { if (live) setPassRows(list); }).catch(() => { if (live) setPassRows([]); });
    return () => { live = false; };
  }, [mode, guestsToPass, project.passKey]);

  const listParsed = useMemo(() => parseBulkLines(input, named), [input, named]);
  const parsed = mode === "passes" ? { rows: passRows, duplicates: 0 } : listParsed;
  const overCap = parsed.rows.length > MAX_BATCH;
  const rows = parsed.rows.slice(0, MAX_BATCH);
  const warning = contrastWarning(fg, bg);
  const longCount = longQrValues(rows);

  const opts = () => ({ errorCorrectionLevel: ec, margin: 4, color: { dark: fg, light: bg } });

  // Gated before the loop starts (the usage check must not sit on an
  // in-progress flag, or the daily limit is never enforced).
  const generate = async () => {
    if (rows.length === 0) return;
    const allowed = await logUsageNow();
    if (!allowed) return;
    setGenerating(true);
    setProgress(0);
    try {
      const zip = new JSZip();
      const px = Number(size);
      for (let i = 0; i < rows.length; i++) {
        const { name, value } = rows[i];
        const file = batchFileName(i, rows.length, name || value, fmt);
        if (fmt === "svg") {
          zip.file(file, await QRCode.toString(value, { ...opts(), type: "svg" }));
        } else {
          const canvas = document.createElement("canvas");
          await QRCode.toCanvas(canvas, value, { ...opts(), width: px });
          const blob = await new Promise<Blob>((r, rej) => canvas.toBlob(b => (b ? r(b) : rej(new Error("png"))), "image/png"));
          zip.file(file, blob);
        }
        if (i % 5 === 0 || i === rows.length - 1) {
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          await new Promise(r => setTimeout(r, 0)); // let the bar paint
        }
      }
      zip.file("index.csv", toCsv(
        rows.map((r, i) => ({ file: batchFileName(i, rows.length, r.name || r.value, fmt), name: r.name ?? "", value: r.value })),
        [{ key: "file", header: "file" }, { key: "name", header: "name" }, { key: "value", header: "value" }],
      ));
      const out = await zip.generateAsync({ type: "blob" });
      downloadBlob(out, "qr-codes.zip");
      toast.success(`Generated ${rows.length} QR codes in one ZIP`);
    } catch {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <ToolContainer>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant={mode === "list" ? "default" : "outline"} onClick={() => setMode("list")} data-testid="bulk-mode-list">My own list</Button>
          <Button size="sm" variant={mode === "passes" ? "default" : "outline"} onClick={() => setMode("passes")} data-testid="bulk-mode-passes">Guest passes from my project ({project.data.guests.length})</Button>
        </div>
        {mode === "passes" ? (
          <div className="space-y-2">
            <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Which guests" value={who} onChange={e => setWho(e.target.value as BadgeWho)}>
              {(Object.keys(BADGE_WHO_LABEL) as BadgeWho[]).map(k => <option key={k} value={k}>{BADGE_WHO_LABEL[k]}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">One signed pass per guest, named after the guest. The code holds a random ID and a signature, never a name or email. Save the project file afterwards: its key is what lets the QR Scanner confirm a pass is genuine.</p>
          </div>
        ) : (
        <div>
          <Label>Enter URLs/text (one per line)</Label>
          <Textarea aria-label="Enter URLs/text (one per line)"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={named ? "Shop,https://example.com/shop\nBlog,https://example.com/blog" : "https://example.com\nhttps://google.com\nHello World"}
            className="min-h-[200px] font-mono"
          />
        </div>
        )}
        {mode === "list" && <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={named} onChange={e => setNamed(e.target.checked)} />
          Lines are <code>name,value</code> (the name is used in the file name)
        </label>}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <Label>Format</Label>
            <Select value={fmt} onValueChange={v => setFmt(v as "png" | "svg")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="png">PNG</SelectItem>
                <SelectItem value="svg">SVG (vector)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>PNG size</Label>
            <Select value={size} onValueChange={setSize} disabled={fmt === "svg"}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["300", "600", "1000", "2000"].map(s => <SelectItem key={s} value={s}>{s} px</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Error correction</Label>
            <Select value={ec} onValueChange={v => setEc(v as "L" | "M" | "Q" | "H")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="L">L (7%)</SelectItem>
                <SelectItem value="M">M (15%)</SelectItem>
                <SelectItem value="Q">Q (25%)</SelectItem>
                <SelectItem value="H">H (30%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Code colour</Label>
            <input type="color" value={fg} onChange={e => setFg(e.target.value)} className="h-9 w-full rounded border" aria-label="Code colour" />
          </div>
          <div>
            <Label>Background</Label>
            <input type="color" value={bg} onChange={e => setBg(e.target.value)} className="h-9 w-full rounded border" aria-label="Background colour" />
          </div>
        </div>
        {longCount > 0 && <NoticeBox className="text-sm" role="status" testId="bulk-long-warning">{longCount} value{longCount === 1 ? " is" : "s are"} longer than 300 characters. That makes a dense code that may not scan. Use a larger export size (PNG 1000 or 2000 px, or SVG) or a lower error correction level, or shorten the value.</NoticeBox>}
        {warning && <NoticeBox className="text-sm" role="alert" testId="bulk-contrast-warning">{warning}</NoticeBox>}

        <p className="text-sm text-muted-foreground" data-testid="bulk-count">
          {rows.length} QR code{rows.length === 1 ? "" : "s"} will be generated
          {parsed.duplicates > 0 && ` (${parsed.duplicates} duplicate line${parsed.duplicates === 1 ? "" : "s"} skipped)`}
        </p>
        {overCap && (
          <p role="alert" className="text-sm text-destructive" data-testid="bulk-cap">
            Batches are limited to {MAX_BATCH} codes. Only the first {MAX_BATCH} of your {parsed.rows.length} will be generated; run the rest as a second batch.
          </p>
        )}

        {rows.length > 0 && (
          <PreviewGrid rows={rows.slice(0, PREVIEW_COUNT)} more={rows.length - PREVIEW_COUNT} fg={fg} bg={bg} ec={ec} />
        )}

        {generating && (
          <div role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} className="h-2 w-full rounded bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        <Button onClick={generate} disabled={generating || rows.length === 0}>
          {generating ? `Generating... ${progress}%` : "Generate ZIP"}
        </Button>
      </div>
      <ProjectBar />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}

function PreviewGrid({ rows, more, fg, bg, ec }: { rows: { name: string | null; value: string }[]; more: number; fg: string; bg: string; ec: string }) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const key = rows.map(r => r.value).join("\n") + fg + bg + ec;
  useEffect(() => {
    let cancelled = false;
    Promise.all(rows.map(async r => [r.value, await QRCode.toDataURL(r.value, { width: 120, margin: 2, errorCorrectionLevel: ec as "L", color: { dark: fg, light: bg } })] as const))
      .then(list => { if (!cancelled) setUrls(Object.fromEntries(list)); })
      .catch(() => { if (!cancelled) setUrls({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">Preview</p>
      <div className="flex flex-wrap gap-2" data-testid="bulk-preview">
        {rows.map((r, i) => urls[r.value] ? (
          <img key={i} src={urls[r.value]} alt={`QR for ${r.value}`} title={r.value} width={72} height={72} className="rounded border" />
        ) : null)}
        {more > 0 && <span className="self-center text-xs text-muted-foreground">+{more} more</span>}
      </div>
    </div>
  );
}
